import type { RuntimeLintContext, RuntimeLintRule } from './runtimeRules';

/**
 * How far a colour channel may vary across a frame and still be one flat colour. Anti-aliasing and
 * a gradient both clear it, so only a frame with nothing drawn on it falls under.
 */
const FLAT_CHANNEL_SPREAD = 16;

export function isOneFlatColour(data: Uint8ClampedArray): boolean {
    const low = [255, 255, 255];
    const high = [0, 0, 0];
    for (let index = 0; index < data.length; index += 4) {
        for (let channel = 0; channel < 3; channel++) {
            const value = data[index + channel];
            low[channel] = Math.min(low[channel], value);
            high[channel] = Math.max(high[channel], value);
            if (high[channel] - low[channel] > FLAT_CHANNEL_SPREAD) {
                return false;
            }
        }
    }
    return true;
}

/**
 * A composition that rasterizes as one flat colour at every probe. Every other rule measures the
 * DOM, which is laid out fine, so without this the graphic passes lint while the preview, the
 * captures and the render are all empty — and checks that read the raster blame whatever they were
 * looking at. Only reported when every frame is flat, since a graphic may open on an empty frame.
 */
export const compositionRendersNothing: RuntimeLintRule = {
    id: 'composition-renders-nothing',
    severity: 'error',
    guidance:
        'Give `html` and `body` an explicit width and height the size of the canvas: a page whose ' +
        'content is all absolutely placed has no size of its own, and is captured as nothing.',
    async check(context: RuntimeLintContext): Promise<string[]> {
        const frames = context.frames ?? [];
        if (frames.length === 0) return [];
        for (const frame of frames) {
            let data: Uint8ClampedArray;
            try {
                const canvas = await frame.rasterize();
                const context2d = canvas.getContext('2d');
                if (!context2d) return [];
                ({ data } = context2d.getImageData(0, 0, canvas.width, canvas.height));
            } catch {
                return []; // a frame that won't rasterize says nothing about what it draws
            }
            if (!isOneFlatColour(data)) return [];
        }
        return [
            'Every frame of this composition renders as one flat colour — nothing on it is drawn, ' +
                'though its elements are laid out. Usually `html` and `body` have no size: give them ' +
                'an explicit width and height the size of the canvas.',
        ];
    },
};
